/**
 * A `teams` room's own numbers: the two sides, what they look like, and the round they play.
 *
 * Its own file rather than a block in `simulation.ts`, on `deathmatch.ts`' and `votes.ts`'
 * precedent: what is here is a *mode's* rules — a colour, a round length, a rule about seat
 * counts — rather than values the growth curve or the wire was tuned against. Everything it needs
 * from the simulation is imported, so there is still one definition of a tick and one of a round.
 */

import type { RoomMode, TeamId } from '../types/index.js';
import { TICK_RATE_HZ } from './simulation.js';

/** Both sides, in the order a scoreboard lists them. Red leads because red is `teamOf`'s first. */
export const TEAM_IDS = ['red', 'blue'] as const satisfies readonly TeamId[];

/** What each side is called on screen. The colour IS the name — see `TeamId`. */
export const TEAM_LABELS: Record<TeamId, string> = {
  red: 'Red',
  blue: 'Blue',
};

/**
 * The hue each side's blobs are drawn at, on the room's own 0..359 wheel.
 *
 * **A TEAM'S COLOUR REPLACES THE PLAYER'S OWN AND IS NOT A SECOND MARK BESIDE IT.** Every blob in
 * a classic room wears a hue drawn per player, and the one thing a player in a team room has to be
 * able to do is tell their side from the other one at a glance across an arena — so the hue is
 * what says it, and a badge on top of a hue nobody could read would be the wrong way round.
 *
 * They are 0 and 220 rather than anything subtler because the wheel is a wheel: those are as far
 * apart as two hues get while still being nameable as "red" and "blue", which is what the mode is
 * called in front of the player. `hueColor` is what turns either into a colour, so both are
 * legible on both themes for free — the same function every pellet and every blob already goes
 * through.
 */
export const TEAM_HUES: Record<TeamId, number> = {
  red: 0,
  blue: 220,
};

/**
 * How long a team round runs before the clock decides it, in seconds.
 *
 * Andy: "the team that remains at the end of the 5 minutes with the biggest mass total for the
 * whole team, OR who just remains because everyone on the other team dies, is the winning team."
 *
 * **It is the DEFAULT rather than the rule.** `RoomSettings.roundSeconds` is what a team room
 * actually reads, exactly as a deathmatch room does, so an operator may run a three-minute room or
 * a ten-minute one; this is what a declaration that says nothing gets. Five minutes rather than
 * the deathmatch default's because a team round ends by elimination far less often — two sides
 * grind toward each other where a free-for-all collapses to one body — so the clock is the
 * ordinary way a team round finishes rather than the backstop.
 */
export const TEAM_ROUND_SECONDS = 5 * 60;
export const TEAM_ROUND_TICKS = Math.round(TEAM_ROUND_SECONDS * TICK_RATE_HZ);

/**
 * **HOW MANY PLAYERS ONE MERGED BLOB HOLDS.**
 *
 * **IT WAS TWO, AND THE ARGUMENT FOR TWO WAS OVERRULED BY THE PERSON IT WAS PROTECTING.** The case
 * against a third was that a passenger with no role is a player who is not playing, and that a
 * join resetting the clock could hold them there for an unbounded time. Andy has read that and
 * asked for it anyway: "I should be able to merge with more users and it keeps adding them but the
 * 3rd+ just keeps coming along for the ride and resetting the 30 second counter." It is his game,
 * the ride is opt-in — nobody is merged against their will — and the growth it buys is real.
 *
 * Six rather than unbounded because the *blob* has to stay a thing on a screen: every member's
 * name is drawn on it (see the merged-blob label) and six names is already a paragraph. It is also
 * comfortably above a whole small team, which is the case worth supporting.
 *
 * **The two ROLES are unchanged and are still only two**: `members[0]` drives, `members[1]` holds
 * split and eject, and everybody after them is exactly the passenger Andy describes.
 */
export const TEAM_MERGE_MAX_MEMBERS = 6;

/**
 * How long a merge lasts before it splits itself apart, **from the last member to join it**.
 *
 * Andy: "It does this for 30 seconds and then they automatically split apart by dividing the
 * current circle's mass in 2", and — once a third member was allowed — "the 3rd+ just keeps coming
 * along for the ride and resetting the 30 second counter". So every join puts a full window back
 * on the clock, which is what makes a growing merge worth chasing more people down with and what
 * makes the ride genuinely open-ended.
 */
export const TEAM_MERGE_SECONDS = 30;
export const TEAM_MERGE_TICKS = Math.round(TEAM_MERGE_SECONDS * TICK_RATE_HZ);

/**
 * How fast a merged blob grows, in mass a second, on top of anything it eats.
 *
 * Andy: "while they're together, their circle grows rather quickly compared to getting dots." It
 * is the whole reward for merging — being one body is otherwise strictly worse than being two,
 * because two bodies cover twice the field and a merged pair has one player who cannot steer.
 *
 * **THIS MASS IS CREATED RATHER THAN MOVED, and that is worth saying out loud** because almost
 * nothing else in this game does it: a duel, a vote and a star are all zero-sum, and a shatter and
 * an eject only ever lose mass. The pellet field is the one existing source, and this is the
 * second. What bounds it is the clock: `TEAM_MERGE_SECONDS` of it and no more, once, per merge.
 *
 * 30 a second is six pellets a second at PELLET_MASS, which is several times what grazing pays —
 * "rather quickly" — and 900 over a full window, which is a real prize and not a run-ender.
 *
 * **IT IS PER EXTRA MEMBER**, so a pair earns this and a trio earns twice it: Andy's "if a 3rd
 * circle joins, it grows that much faster". The multiplier is `members - 1` rather than `members`
 * so that the pair — the ordinary case — is exactly the rate above and not half of it.
 */
export const TEAM_MERGE_GROWTH_PER_SECOND = 30;

/**
 * How far apart the halves are placed when a merge comes apart, as a multiple of a half's radius.
 *
 * Far enough that the two are not instantly overlapping — they are different players, so nothing
 * would fuse them, but two bodies born inside each other would spend their first seconds pushing
 * apart — and close enough that the pair is still together when it happens.
 */
export const TEAM_MERGE_SPLIT_SPREAD = 1.2;

/**
 * True for a room whose players wear a side: `teams` and `zombie`.
 *
 * The one predicate the two share, and it is what every rule about *sides* is written against —
 * the split at `beginRound`, the eat rule, the colours, the scoreboard. What separates them is
 * what happens when a body runs out, which is `RoomSim`'s own branch and not this.
 */
export function roomHasTeams(mode: RoomMode): boolean {
  return mode === 'teams' || mode === 'zombie';
}

/**
 * Which side a hue belongs to, or null for a hue that is nobody's.
 *
 * **THE HUE *IS* THE TEAM IN A TEAMS ROUND, AND THAT IS A GUARANTEE RATHER THAN A COINCIDENCE.**
 * `beginRound` writes `TEAM_HUES[team]` onto every player it seats and `fillTeamBots` does the
 * same for every bot, so in a `teams` room a player's hue is exactly one of these two values —
 * which is what lets a client work out somebody's side with **nothing on the wire at all**.
 *
 * It must only be asked in a room that is actually running teams: in a classic room a hue is
 * drawn per player and one of them will eventually roll 0. `MatchView.teams` is what says which
 * kind of room this is, and it is the caller's job to check it.
 */
export function teamOfHue(hue: number): TeamId | null {
  for (const team of TEAM_IDS) {
    if (TEAM_HUES[team] === hue) return team;
  }
  return null;
}

/**
 * The other side. Total, because there are exactly two — see `TeamId` for why that is the
 * mechanic rather than a limit worth generalising.
 */
export function opposingTeam(team: TeamId): TeamId {
  return team === 'red' ? 'blue' : 'red';
}

/**
 * **A TEAM ROOM'S SEAT COUNT MUST BE EVEN**, which is the one rule the mode puts on a declaration.
 *
 * Andy: "We should be able to set the number of players in the room and it must be a multiple of 2
 * (and even number)." An odd cap cannot be split down the middle, so a room declared with one
 * would be a room whose two sides are permanently unequal — which is the one thing a team game may
 * not be, because every other rule in the mode is a comparison between them.
 *
 * The *lobby* is allowed to be odd and is dealt with where it happens: an odd number of humans
 * puts the spare on a side drawn from the room's own stream, and the bot fill then evens the two
 * up. That is a fact about who turned up rather than about how the room was declared, so it is
 * settled at `beginRound` and not here.
 */
export function isValidTeamSeatCount(maxPlayers: number): boolean {
  return Number.isInteger(maxPlayers) && maxPlayers >= 2 && maxPlayers % 2 === 0;
}
